/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable no-nested-ternary */
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { AlertTriangle, X } from 'react-feather';
import styles from './EditPriceModifierDrawer.module.css';
import Tabs from '@/components/Tabs/Tabs';
import { t } from 'i18next';
import MaskedInput from '@/components/MaskedInput/MaskedInput';
import { useUpdateProductPrice, useVariants } from '@/services/modifiers/modifiers.service';
import { useSTKContext } from '@/context/context';
import {
  EditDrawerSuccessAndFailure,
  EntityToUpdate,
} from '@/pages/Franchisee/SharedComponents/EditDrawerSuccessAndFailure';
import {
  checkIfHasVariants,
  combineStoreGroupPrices,
  getStoreGroups,
  getStores,
  getUniqStores,
  isPriceInvalid,
} from '@/services/modifiers/modifiers.helper.service';
import { InputStates } from '@/components/Input/Input.types';
import { Typography } from '@mui/material';
import Button from '@/components/Button/Button';
import { VariantModifier, VariantModifierPriceUpdateRequest } from '@/types/menus.types';
import _ from 'lodash';
import useFranchiseeAlias from '@/utils/useFranchiseeAlias/useFranchiseeAlias';
import { EditDrawerProcessing } from '../../SharedComponents/EditDrawerProcessing';
import { EditPriceModifierDrawerProps } from '@/pages/Franchisee/Menus/EditPriceDrawer/EditPriceModifierDrawerProps.types';
import CurrentModifierPrice from '@/pages/Franchisee/Menus/EditPriceDrawer/EditPriceTabItems/CurrentModifierPrice';

import { TabProductsModifiersExcluded } from '@/pages/Franchisee/Menus/EditPriceDrawer/Internal/TabProductsModifiersExcluded';
import { ProductsModifierIncluded } from '@/pages/Franchisee/Menus/EditPriceDrawer/Internal/EditPriceModifierTabItems';

function buildVariantPriceRequest(
  variants: VariantModifier[],
  nonUpdatedStores: string[],
  inputPrice: string,
  categoryName: string,
): VariantModifierPriceUpdateRequest[] {
  return variants.map((variant) => ({
    stores: _.difference(
      variants.filter((sp1) => sp1.variantId === variant.variantId).flatMap((sp) => sp.variantStores),
      nonUpdatedStores,
    ), //list of store strings
    modifierKeys: {
      variantId: variant.variantId,
      slotId: variant.slotId,
      modifierId: variant.modifierId,
      modifierWeightId: variant.modifierWeightId,
    },
    price: inputPrice.replace('$', ''),
    modifierCategory: categoryName,
  }));
}

export const EditPriceModifierDrawer: FC<EditPriceModifierDrawerProps> = ({
  isOpen,
  onClose,
  storeModifiers,
  afterSave,
  setPriceDeviationsData,
  categoryName,
}) => {
  const modifierKeys = storeModifiers
    .filter((prod) => !prod.priceIsLocked)
    .filter((sp) => !!sp.variantId)
    .map((sp) => ({
      variantId: sp.variantId,
      modifierId: sp.modifierId,
      modifierWeightId: sp.modifierWeightId,
      slotId: sp.slotId,
    }));
  const { storesAndGroups } = useSTKContext();
  const [state, setState] = useState(isOpen);
  const [priceIsSaved, setPriceIsSaved] = useState(false);
  const [inputPriceState, setInputPriceState] = useState(InputStates.Default);
  const [stores, setStores] = useState<string[]>(getStores(storesAndGroups));
  const [storeGroups, setStoreGroups] = useState(getStoreGroups(storesAndGroups));
  const [nonUpdatedStores, setNonUpdatedStores] = useState<string[]>([]);
  const { alias } = useFranchiseeAlias();
  const { data: variants = [] } = useVariants(true, stores, storeGroups, modifierKeys, categoryName);
  const {
    mutateAsync: updateProductPrice,
    data: updatePriceResponse,
    isLoading: isSavingPrice = false,
  } = useUpdateProductPrice();

  const [activeTab, setActiveTab] = useState(0);
  const [inputPrice, setInputPrice] = useState('');
  useEffect(() => {
    if (!isOpen) {
      setActiveTab(0);
      setInputPrice('');
    }
    setState(isOpen);
  }, [isOpen]);

  useEffect(() => {}, [variants]);

  const handleTabClick = (indexOfClickedTab: number) => {
    setActiveTab(indexOfClickedTab);
  };

  const currentModifiers = storeModifiers;
  const data = combineStoreGroupPrices(currentModifiers, storesAndGroups);
  const [tableData, setTableData] = useState(data);
  const sortedTableData = tableData.sort((a, b) => a.storeGroupName.localeCompare(b.storeGroupName));
  const minValidPrices: number[] = [];
  const storeNames = tableData.map((store) => store.storeGroupName);

  currentModifiers.forEach((modifier) => {
    modifier.storeGroups.forEach((sGroup) => {
      if (storeNames.includes(sGroup.storeGroupName) && storeNames.length > 0) {
        minValidPrices.push(Number(sGroup?.chosenPriceRange));
      }
    });
  });

  const maxValidPrices: number[] = [];

  currentModifiers.forEach((modifier) => {
    modifier.storeGroups.forEach((sGroup) => {
      if (storeNames.includes(sGroup.storeGroupName) && storeNames.length > 0) {
        maxValidPrices.push(Number(sGroup?.chosenPriceRange));
      }
    });
  });

  const handlePriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setInputPrice(value);
  };
  const lowestValidPrice = '0.00';
  const highestValidPrice = '5.00';

  const saveModifier = () => {
    if (alias === null && isPriceInvalid(inputPrice, lowestValidPrice, highestValidPrice)) {
      setInputPriceState(InputStates.Error);
    } else {
      const justNotLockedVariants = variants.filter((variant) => !variant.variantLock);
      const variantModifierPriceUpdateRequest = buildVariantPriceRequest(
        justNotLockedVariants,
        nonUpdatedStores,
        inputPrice,
        categoryName,
      );

      updateProductPrice(variantModifierPriceUpdateRequest, {
        onSuccess: () => {
          setPriceIsSaved(true);
          afterSave();
        },
        onError: () => {},
      });
    }
  };

  const onCloseDrawer = () => () => {
    setPriceIsSaved(false);
    setInputPriceState(InputStates.Default);
    setInputPrice('');
    setState(false);
    onClose();
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 1:
        return <ProductsModifierIncluded modifiers={currentModifiers.filter((modifier) => !modifier.priceIsLocked)} />;

      case 2:
        return <TabProductsModifiersExcluded variants={variants} />;

      default:
        return (
          <CurrentModifierPrice
            tableData={sortedTableData}
            setPriceDeviationsData={setPriceDeviationsData}
            modifiers={storeModifiers}
            modifierCategory={categoryName}
          />
        );
    }
  };

  const generateTabs = () => {
    const hasExcludedStores = checkIfHasVariants(variants);

    const tabs = [
      { label: t('editMenuPrice.currentPrice'), id: 'currentPrice' },
      { label: t('editMenuPrice.modifiersIncluded'), id: 'modifiersIncluded' },
    ];

    if (hasExcludedStores) {
      tabs.push({
        label: t('editMenuPrice.modifiersExcluded'),
        id: 'modifiersExcluded',
      });
    }

    return tabs;
  };
  const uniqueStores = useMemo(
    () => _.difference(getUniqStores(variants), nonUpdatedStores),
    [variants, nonUpdatedStores],
  );

  return (
    <div>
      <SwipeableDrawer open={state} onClose={onCloseDrawer()} onOpen={() => {}} anchor="bottom">
        <div className={styles.container}>
          <header>
            <div>
              <h1>{t('editMenuPrice.editPrice')}</h1>
            </div>
            <button type="button" onClick={onCloseDrawer()}>
              <X data-testid="x-icon" size={24} color="var(--black-900)" />
            </button>
          </header>
          <div className={styles.content}>
            {isSavingPrice ? (
              <EditDrawerProcessing count={uniqueStores.length} entityToUpdate={EntityToUpdate.MENU} />
            ) : priceIsSaved ? (
              <EditDrawerSuccessAndFailure apiResponse={updatePriceResponse} entityToUpdate={EntityToUpdate.MENU} />
            ) : (
              <>
                <div className={styles.editPrice}>
                  <strong>{t('editMenuPrice.newPrice')}</strong>
                  <div className={styles.info}>
                    <div className={styles.input}>
                      <MaskedInput
                        className={styles.input}
                        id="new-price"
                        label={t('editMenuPrice.enterPricePoint')}
                        prefix="$"
                        fixedDecimalScale
                        decimalScale={2}
                        value={inputPrice}
                        onChange={handlePriceChange}
                        state={inputPriceState}
                      />
                      <Button
                        data-testid="edit-price"
                        disabled={tableData.length === 0}
                        onClick={() => {
                          saveModifier();
                        }}
                        type="button"
                      >
                        Submit
                      </Button>
                    </div>
                  </div>

                  <Typography
                    mt={2}
                    sx={{
                      fontWeight: '400',
                      fontSize: '12px',
                      color: `${inputPriceState === InputStates.Error ? 'var(--red-700)' : ''}`,
                    }}
                  >
                    {t('editMenuPrice.priceRange')}: ${lowestValidPrice} - ${highestValidPrice}
                  </Typography>
                </div>
                <div className={styles.tableContainer}>
                  <Tabs
                    activeTab={activeTab}
                    onTabClick={handleTabClick}
                    tabs={generateTabs().map((tab) => ({
                      ...tab,
                      ...(tab.id === 'modifiersExcluded' && { icon: <AlertTriangle size="16px" color="red" /> }),
                    }))}
                  />
                  <div className={styles.tabContainer}>{renderTabContent()}</div>
                </div>
              </>
            )}
          </div>
        </div>
      </SwipeableDrawer>
    </div>
  );
};
